import BaseContainer from "@base/container/BaseContainer";
import gsap from "gsap";
import {Signals} from "@/Imports";
import PlaneConfig from "@/config/PlaneConfig";
import Facade from "@/Facade";
import * as PIXI from "pixi.js";
import ScaleManager from "@/stage/ScaleManager";
import SpineAnimation from "@base/spineAnimation/SpineAnimation";

export default class PlaneContainer extends BaseContainer {

    constructor(data) {
        super(data)

        this.startPointOffset = {x: 49, y: 28}
        this.endPointOffset = {x: 780, y: 300}

        this.deltaX = 0
        this.deltaY = 0

        this.swing = 0

        this.trackThePath = false
        this.path_index = 0

        this.planeScale = 0.10
        this.arcColor = 0xfedf61
        this.init(data)
    }

    init(data) {
        this.path = this.trackThePath ? [] : PlaneConfig.plane

        Signals.forceRoundStart.add(this.reset, this)
        Signals.makeBets.add(this.onMakeBets, this)
        Signals.roundStart.add(this.launchPlane, this)
        Signals.roundEnd.add(this.onRoundEnd, this)
        // Signals.playerTakeWin.add(this.onPlayerCashOut, this)

        this.arc = new PIXI.Graphics();
        this.arc.name = "Arc Graphic"
        this.addChild(this.arc);
        this.arc.alpha = 0

        this.arc_mask = new PIXI.Graphics()
        this.addChild(this.arc_mask)

        this.plane = new SpineAnimation('plane')
        this.plane.start(['fly_1'], [true])
        this.plane.scale.set(0.5)
        this.addChild(this.plane)
        this.updatePlane()
    }

    reset() {
        this.hideArc()

        this.deltaX = this.startPointOffset.x;
        this.deltaY = this.startPointOffset.y;
        this.plane.rotation = 0
        this.plane.alpha = 1
        this.swing = 0
        gsap.killTweensOf(this)

        this.updatePlane()
        this.updateArc()

        this.plane.start(['fly_1'], [true])
    }

    launchPlane() {
        this.isInIdle = false
        this.showArc()

        Facade.soundManager.play('planeLiftoff', 0.5)
        Signals.afterStart.addOnce(this.onAfterStart, this)

        this.plane.start(['fly_1'], [true])
        this.deltaX = this.startPointOffset.x;
        this.deltaY = this.startPointOffset.y;
        this.tilt = 1;
        this.path_index = 0;

        if (!this.trackThePath) {
            this.path_index = Math.round(Facade.model.deltaTime * 60)

            if (Facade.model.deltaTime < 0 || this.path_index >= this.path.length - 1) {
                // The last point on the path
                this.path_index = this.path.length - 1
                const o = this.path[this.path.length - 1]

                this.deltaX = o.x
                this.deltaY = o.y
                this.plane.rotation = o.r

                // this.plane.start(['idle'], [true])
                Signals.afterStart.dispatch()
            } else {
                // this.plane.start(['start', 'idle'], [false, true])
            }
        } else {
            this.path.length = 0
            gsap.to(this, 32, {
                deltaX: `+=${this.endPointOffset.x}`,
                deltaY: `+=${this.endPointOffset.y}`,
                onComplete: () => Signals.afterStart.dispatch(),
                ease: "power4.in"
            })
            gsap.to(this.plane, 10, {
                rotation: -0.2, onComplete: () => {
                    gsap.to(this.plane, 7, {rotation: 0})
                }
            })
        }

        Signals.tick.add(this.updatePath, this)
    }

    updatePath() {
        this.updatePlane()
        this.updateArc()

        if (this.trackThePath) {
            const object_pathToLog = {
                x: Number(this.deltaX.toFixed(2)),
                y: Number(this.deltaY.toFixed(2)),
                r: Number(this.plane.rotation)
            }

            this.path.push(object_pathToLog)
        } else {
            const object_pathFromConfig = this.path[this.path_index]
            this.deltaX = object_pathFromConfig.x
            this.deltaY = object_pathFromConfig.y
            this.plane.rotation = object_pathFromConfig.r

            this.path_index++
            if (this.path_index >= this.path.length) {
                this.path_index = this.path.length - 1
                if (!this.isInIdle) {
                    Signals.afterStart.dispatch()
                }
            }
        }
    }

    updateArc() {
        const showLines = true
        const width = ScaleManager.currentSizes.total.width
        const height = ScaleManager.currentSizes.total.height

        const bottom = height - this.startPointOffset.y;
        const left = this.startPointOffset.x

        const pickX = left + this.deltaX;
        const pickY = bottom - this.deltaY + this.swing;

        const skew = 0 // The straight line right under the plane can be not
        const curve = 3.5

        this.arc.clear();
        this.arc.beginFill(0xfedf61, 0.3);
        this.arc.moveTo(left, bottom)
        this.arc.bezierCurveTo(
            left,
            bottom,
            pickX - this.deltaX / curve,
            bottom - this.deltaY / curve,
            pickX,
            pickY)
        this.arc.lineTo(pickX - skew, bottom);
        this.arc.endFill();

        if (showLines) {
            this.arc_mask.clear()
            this.arc_mask.beginFill(0xff0000, 1);
            this.arc_mask.lineStyle(5, 0x00ff00, 1);
            this.arc_mask.moveTo(left, bottom)
            this.arc_mask.bezierCurveTo(
                left,
                bottom,
                pickX - this.deltaX / curve,
                bottom - this.deltaY / curve,
                pickX,
                pickY)
            this.arc_mask.lineTo(pickX - skew, bottom);
            this.arc_mask.endFill();
            this.arc.mask = this.arc_mask
            this.arc.lineStyle(7, 0xfedf61, 0.03);
            for (let i = left - 300; i < pickX - skew + 300; i += 20) {
                const x = i - this.plane.y
                this.arc.moveTo(pickX, pickY)
                this.arc.lineTo(x, bottom)
            }
            // this.arc.lineStyle(3, 0xfedf61, 0.05);
            // for (let i = pickY; i < bottom; i += 6) {
            //   this.arc.moveTo(left, bottom)
            //   this.arc.lineTo(pickX, i)
            // }
        }

        this.arc.lineStyle(5, 0xfedf61);
        this.arc.moveTo(left, bottom)
        this.arc.bezierCurveTo(
            left,
            bottom,
            pickX - this.deltaX / 3.6,
            bottom - this.deltaY / 3.6,
            pickX,
            pickY)
    }

    updatePlane() {
        this.plane.x = this.deltaX + 24
        this.plane.y = ScaleManager.currentSizes.total.height - this.deltaY + this.swing - this.plane.height - 14
    }

    onAfterStart() {
        this.isInIdle = true
        if (this.trackThePath) {
            console.log('>>> hero path tracking', this.path)
        }
        gsap.to(this, 3, {swing: "-=50", repeat: -1, yoyo: true, ease: "none"});
        this.plane.start(['fly_2'], [true])
    }

    onRoundEnd() {
        gsap.to(this.plane, 2, {
            x: ScaleManager.currentSizes.total.width + 200,
            y: `+=${this.plane.rotation * 1000}`,
            onComplete: () => {
                this.hideArc()
                this.reset()
            }
        })
        gsap.to(this.plane, 0.5, {alpha: 0, delay: 0.5})

        Facade.soundManager.play('planeFlyAway', 0.5)
        Signals.tick.remove(this.updatePath, this)
    }

    onMakeBets() {
        this.updatePlane()
        this.updateArc()
    }

    showArc() {
        gsap.to(this.arc, 2, {alpha: 1})
    }

    hideArc() {
        gsap.to(this.arc, 1, {alpha: 0})
    }

}
